/**
 * The OPR Pricing tab on the patient panel (Treatment Plan spec v1.4, S-03),
 * drawn from canvas board 02: one row per drug on the plan with the pharmacy
 * cost, the clinic's price, the margin, the public prices and OPR, the dark
 * banner, a "Why this price?" panel, and a live phone preview of what the
 * patient will see.
 *
 * It is the same price book as the treatment plan tab (`useOprPrices`, one
 * query key), so a price changed here is on the plan tab as soon as it is
 * saved, and the other way round. The drugs shown are the ones ticked on the
 * plan tab, or the latest plan sent to this patient when nothing is ticked.
 *
 * Cost, margin and OPR live here and nowhere a patient can reach (PG-006). The
 * phone preview shows the patient's view: names, how to take, prices, total.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pricingBanner } from '@mcos/types';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { InfoHint } from './ui';
import { Bell, Menu } from '../pages/portal/icons';
import {
  PricingBanner,
  bannerLines,
  dollars,
  savedPrice,
  toCents,
  useOprPrices,
  usd,
  wholeUsd,
  type OprItem,
} from './OprPricing';

const COST_SOURCE: Record<string, string> = {
  'Public reference cost': 'Public reference',
  'None yet': 'No public cost',
};

const UNIT_WORD: Record<OprItem['billingUnit'], string> = { month: 'month', visit: 'visit', bottle: 'bottle' };

/** Where the clinic's price sits against OPR, as the small line under the box. */
function standing(item: OprItem): { text: string; tone: string } {
  const price = item.clinic.priceCents;
  const opr = item.opr.priceCents;
  if (price == null) return { text: 'Not set', tone: 'text-[#5b6168]' };
  if (opr == null) return { text: 'No OPR yet', tone: 'text-[#5b6168]' };
  if (price === opr) return { text: 'At OPR', tone: 'text-[#1b3f35]' };
  return price > opr ? { text: 'Above OPR', tone: 'text-navy' } : { text: 'Below OPR', tone: 'text-[#8a5a24]' };
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function OprPricingTab({
  patientId,
  firstName,
  lastName,
  selected,
}: {
  patientId: string;
  firstName: string;
  lastName: string;
  /** The treatments ticked on the plan tab; null before that tab has loaded. */
  selected: string[] | null;
}) {
  const prices = useOprPrices();
  // S-20: "a banner on the OPR Pricing tab" while a linked pharmacy's price change is unread. Providers only.
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const notices = useQuery({
    queryKey: ['price-notice-count'],
    queryFn: () => api.get<{ count: number }>('/clinic-pharmacies/notices/count'),
    enabled: provider,
    retry: false,
  });
  const plans = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => api.get<{ plans: Array<{ id: string; status: string; itemIds: string[] }> }>(`/regimens/plans/${patientId}`),
  });
  // Before the plan tab has opened, the same default it will tick: the report's recommended drugs.
  const recommendations = useQuery({
    queryKey: ['recommendations', patientId],
    queryFn: () => api.get<{ recommendations: Array<{ treatmentId: string; recommended: boolean }> }>(`/patients/${patientId}/recommendations`),
    enabled: selected === null,
  });
  const [chosen, setChosen] = useState<string | null>(null);

  const sentPlan = plans.data?.plans.find((plan) => plan.status !== 'draft') ?? null;

  let rows: OprItem[] = [];
  if (selected && selected.length > 0) {
    rows = selected.flatMap((id) => prices.byTreatment.get(id) ?? []);
  } else if (sentPlan) {
    rows = sentPlan.itemIds.flatMap((id) => prices.byItemId.get(id) ?? []);
  } else if (selected === null) {
    rows = (recommendations.data?.recommendations ?? [])
      .filter((entry) => entry.recommended)
      .flatMap((entry) => prices.byTreatment.get(entry.treatmentId) ?? []);
  }

  if (prices.isLoading || plans.isLoading) {
    return <section className="rounded-[10px] border border-hairline bg-white px-5 py-8 text-[14px] text-[#5b6168]">Loading prices...</section>;
  }
  if (prices.error) {
    return <section className="rounded-[10px] border border-hairline bg-white px-5 py-8 text-[14px] text-alert">Could not load the price book.</section>;
  }
  if (rows.length === 0) {
    return (
      <section className="rounded-[10px] border border-dashed border-hairline bg-white px-5 py-10 text-center">
        <p className="font-display text-[18px] text-navy">No drugs to price yet</p>
        <p className="mx-auto mt-1 max-w-md text-[13.5px] text-[#4d6072]">
          Tick treatments on the MCOS Treatment Plan tab and their prices appear here.
        </p>
      </section>
    );
  }

  const focus = rows.find((row) => row.itemId === chosen) ?? rows[0]!;

  return (
    <section className="flex flex-col gap-[18px] rounded-[14px] border border-hairline bg-white p-4 sm:p-6" data-testid="opr-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-[28px] font-semibold leading-tight text-navy">OPR Pricing</h2>
          <p className="mt-1.5 text-[15px] text-[#5b6168]">
            Same prices as the treatment plan tab. Change them here or there; both stay in sync.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void prices.useOprForAll(rows)}
          className="h-11 shrink-0 rounded-[10px] border border-forest bg-white px-4 text-[15px] font-semibold text-forest hover:bg-mint"
        >
          Use OPR for all
        </button>
      </div>
      {(notices.data?.count ?? 0) > 0 && (
        <p className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] border border-[#e3d3a8] bg-[#fffaf0] px-3 py-2 text-[13.5px] text-navy" role="status">
          <span>Your pharmacy has changed its prices. See what it means for your costs before you price this plan.</span>
          <Link to="/settings/pharmacies" className="font-semibold text-forest hover:underline">Open Pharmacies</Link>
        </p>
      )}
      {prices.saveError && (
        <p role="alert" className="rounded-md bg-alert-soft px-3 py-2 text-[13px] text-alert">
          {prices.saveError}
        </p>
      )}

      <div className="overflow-x-auto rounded-[14px] border border-[#e6e3dc]">
        <div className="min-w-[960px]">
          <div className="grid grid-cols-[minmax(0,1.5fr)_160px_180px_110px_minmax(0,1fr)_170px] gap-3.5 border-b border-[#e6e3dc] bg-[#f4f3ee] px-[18px] py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-navy">
            <div>Medication</div>
            <div>Pharmacy cost</div>
            <div>Your price</div>
            <div>Margin</div>
            <div>Public prices</div>
            <div>OPR</div>
          </div>
          {rows.map((item) => (
            <OprRow
              key={item.itemId}
              item={item}
              focused={item.itemId === focus.itemId}
              onFocus={() => setChosen(item.itemId)}
              prices={prices}
            />
          ))}
        </div>
      </div>

      <PricingBanner items={rows} firstName={firstName} />

      <div className="flex flex-wrap items-start gap-6">
        <WhyPanel item={focus} />
        <PhonePreview rows={rows} firstName={firstName} lastName={lastName} />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * One row
 * ------------------------------------------------------------------ */

function OprRow({
  item,
  focused,
  onFocus,
  prices,
}: {
  item: OprItem;
  focused: boolean;
  onFocus: () => void;
  prices: ReturnType<typeof useOprPrices>;
}) {
  const [costText, setCostText] = useState<string | null>(null);
  const [priceText, setPriceText] = useState<string | null>(null);

  const price = item.clinic.priceCents;
  const saved = savedPrice(item);
  const cost = item.clinic.costCents;
  const opr = item.opr.priceCents;
  const own = item.clinic.ownCostCents;
  const stand = standing(item);

  const commitCost = () => {
    if (costText === null) return;
    const typed = costText.trim() === '' ? null : toCents(costText);
    setCostText(null);
    if (typed !== own) prices.save(item.itemId, { costCents: typed });
  };
  const commitPrice = () => {
    if (priceText === null) return;
    const typed = priceText.trim() === '' ? null : toCents(priceText);
    setPriceText(null);
    prices.setDraft(item.itemId, undefined);
    if (typed !== saved) prices.save(item.itemId, { priceCents: typed });
  };

  return (
    <div
      className={`grid grid-cols-[minmax(0,1.5fr)_160px_180px_110px_minmax(0,1fr)_170px] items-center gap-3.5 border-b border-[#efede7] px-[18px] py-3.5 last:border-b-0 ${
        focused ? 'bg-[#fafcfa]' : 'bg-white'
      }`}
      data-testid={`opr-row-${item.itemId}`}
    >
      <div>
        <div className="flex items-center gap-1.5">
          <span className="font-display text-[18px] font-semibold leading-snug text-navy">{item.name}</span>
          {item.needsPrescriberJustification && (
            <InfoHint label="Needs a patient specific reason">
              The prescriber gives a patient specific clinical reason before this drug can be sent.
            </InfoHint>
          )}
        </div>
        {item.doseLine && <div className="text-[13px] text-[#5b6168]">{item.doseLine}</div>}
        <button type="button" onClick={onFocus} className="mt-0.5 py-1 text-[13px] font-semibold text-forest underline">
          Why this price?
        </button>
      </div>

      <div>
        <label className="flex h-[42px] items-center gap-[3px] rounded-[9px] border border-[#d8d3c8] bg-white px-2.5">
          <span className="text-[15px] text-[#5b6168]">$</span>
          <input
            inputMode="decimal"
            aria-label={`Pharmacy cost for ${item.name}`}
            className="w-[70px] min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] font-medium text-navy outline-none placeholder:text-[#a3a8ad]"
            placeholder={own == null && cost != null ? dollars(cost) : 'enter'}
            value={costText ?? dollars(own)}
            onFocus={() => setCostText(dollars(own))}
            onChange={(event) => setCostText(event.target.value)}
            onBlur={commitCost}
            onKeyDown={(event) => event.key === 'Enter' && (event.target as HTMLInputElement).blur()}
          />
        </label>
        <div className="mt-[3px] text-[12px] text-[#5b6168]">{item.clinic.costSourceLine ?? COST_SOURCE[item.clinic.costSource] ?? item.clinic.costSource}</div>
      </div>

      <div>
        <label
          className={`flex h-[42px] items-center gap-[3px] rounded-[9px] border bg-white px-2.5 ${
            price != null ? 'border-solid border-forest' : 'border-dashed border-[#b9b4a8]'
          }`}
        >
          <span className={`text-[16px] font-semibold ${price != null ? 'text-navy' : 'text-[#a3a8ad]'}`}>$</span>
          <input
            inputMode="decimal"
            aria-label={`Your price for ${item.name}`}
            className="w-[60px] min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] font-semibold text-navy outline-none placeholder:text-[#a3a8ad]"
            placeholder={opr != null ? dollars(opr) : 'Price'}
            value={priceText ?? dollars(price)}
            onFocus={() => setPriceText(dollars(saved))}
            onChange={(event) => {
              setPriceText(event.target.value);
              prices.setDraft(item.itemId, event.target.value.trim() === '' ? null : toCents(event.target.value));
            }}
            onBlur={commitPrice}
            onKeyDown={(event) => {
              if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
              // Tab on an empty box accepts the faded OPR price (PR-004).
              if (event.key === 'Tab' && price == null && !(priceText ?? '').trim() && opr != null) {
                setPriceText(null);
                prices.setDraft(item.itemId, undefined);
                prices.save(item.itemId, { priceCents: opr });
              }
            }}
          />
          <span className="text-[12px] text-[#5b6168]">{item.unitLabel}</span>
        </label>
        <div className={`mt-[3px] flex items-center gap-2 text-[12px] ${stand.tone}`}>
          <span>{stand.text}</span>
          {price == null && opr != null && (
            <button type="button" className="font-semibold text-forest underline" onClick={() => prices.save(item.itemId, { priceCents: opr })}>
              Use
            </button>
          )}
        </div>
      </div>

      <div className="text-[16px] font-semibold text-navy">
        {price != null && cost != null ? `${wholeUsd(price - cost)}${item.clinic.estimated ? ' est.' : ''}` : '-'}
      </div>

      <div className="text-[14px] leading-[1.4]">
        {item.opr.rangeLowCents != null && item.opr.rangeHighCents != null ? (
          <>
            {wholeUsd(item.opr.rangeLowCents)} to {wholeUsd(item.opr.rangeHighCents)}
            <div className="text-[12px] text-[#5b6168]">{plural(item.public.patientPriceCount, 'public patient price', 'public patient prices')}</div>
          </>
        ) : (
          <span className="text-[#5b6168]">No public prices</span>
        )}
      </div>

      <div>
        <div className="text-[16px] font-semibold text-navy">{opr != null ? `${usd(opr)}${item.unitLabel}` : 'None yet'}</div>
        <span className="mt-[3px] inline-block rounded-[5px] bg-[#f1f0eb] px-[7px] py-0.5 text-[12px] text-navy">
          {item.opr.label} · {item.opr.confidence === 'n/a' ? 'n/a' : item.opr.confidence.charAt(0).toUpperCase() + item.opr.confidence.slice(1)}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Why this price?
 * ------------------------------------------------------------------ */

function WhyPanel({ item }: { item: OprItem }) {
  const opr = item.opr.priceCents;
  const you = item.clinic.priceCents;
  const dots = item.public.patientPricesCents;
  const unit = UNIT_WORD[item.billingUnit];

  // The strip runs a little past the lowest and highest public price, as drawn.
  const low = dots.length > 0 ? Math.min(...dots) : null;
  const high = dots.length > 0 ? Math.max(...dots) : null;
  const pad = low != null && high != null ? Math.max(1, Math.round((high - low) * 0.08)) : 0;
  const lo = low != null ? low - pad : 0;
  const hi = high != null ? high + pad : 1;
  const at = (cents: number) => `${Math.max(0, Math.min(100, ((cents - lo) / (hi - lo)) * 100)).toFixed(1)}%`;

  const progress = Math.min(100, Math.round((item.learning.plansSent / item.learning.target) * 100));

  return (
    <div className="flex min-w-[300px] flex-1 flex-col gap-[18px] rounded-[14px] border border-[#e6e3dc] p-[22px]" data-testid="why-panel">
      <div className="flex flex-col gap-2.5">
        <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-forest">Why this price?</div>
        <div className="font-display text-[22px] font-semibold leading-tight text-navy">
          {opr != null ? `${item.opr.label} for ${item.name}: ${usd(opr)}${item.unitLabel}` : `No starting price for ${item.name} yet`}
        </div>
        <div className="text-[14px] leading-normal text-[#5b6168]">{item.opr.basisText}</div>
        <div className="text-[14px] leading-normal text-navy">
          Based on {plural(item.public.patientPriceCount, 'public patient price', 'public patient prices')} and{' '}
          {plural(item.public.pharmacyPriceCount, 'public pharmacy price', 'public pharmacy prices')}
          {item.public.asOf ? ` (${item.public.asOf})` : ''}, plus your cost when you enter it.
        </div>
      </div>

      <div className="flex flex-col gap-3.5">
        <div className="text-[14px] font-semibold text-navy">Public patient prices, per {unit}</div>
        {dots.length > 0 ? (
          <>
            <div className="relative mx-2 h-[70px]">
              <div className="absolute inset-x-0 top-[34px] h-0.5 bg-[#d8d3c8]" />
              {dots.map((cents, index) => (
                <span
                  key={`${cents}-${index}`}
                  className="absolute top-7 -ml-[7px] h-3.5 w-3.5 rounded-full border-2 border-white bg-[#8da99c]"
                  style={{ left: at(cents) }}
                />
              ))}
              {opr != null && (
                <>
                  <span className="absolute bottom-1.5 top-1.5 w-0.5 bg-forest" style={{ left: at(opr) }} />
                  <span className="absolute -top-3 -translate-x-1/2 whitespace-nowrap text-[12px] font-semibold text-forest" style={{ left: at(opr) }}>
                    OPR {usd(opr)}
                  </span>
                </>
              )}
              {you != null && (
                <>
                  <span className="absolute top-[26px] -ml-[9px] h-[18px] w-[18px] rounded-full border-2 border-white bg-[#1c2b3a]" style={{ left: at(you) }} />
                  <span className="absolute top-[52px] -translate-x-1/2 whitespace-nowrap text-[12px] font-semibold text-navy" style={{ left: at(you) }}>
                    You {usd(you)}
                  </span>
                </>
              )}
            </div>
            <div className="flex justify-between text-[12px] text-[#5b6168]">
              <span>{wholeUsd(lo)}</span>
              <span>{wholeUsd(hi)}</span>
            </div>
          </>
        ) : (
          <p className="text-[13px] text-[#5b6168]">No public patient prices to compare with yet.</p>
        )}

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
          <div className="rounded-[10px] border-2 border-forest bg-[#e6f0ea] p-3">
            <div className="text-[13px] font-semibold text-[#1b3f35]">Now · Starting price</div>
            <div className="mt-1 text-[13px] leading-[1.45] text-[#1b3f35]">Public research prices + your cost</div>
          </div>
          <div className="rounded-[10px] border border-[#e6e3dc] p-3">
            <div className="text-[13px] font-semibold text-navy">Next · Learns from your clinic</div>
            <div className="mt-1 text-[13px] leading-[1.45] text-[#5b6168]">
              After {item.learning.target} plans with this drug · {item.learning.plansSent} of {item.learning.target} so far
            </div>
            <div className="mt-2 h-1.5 rounded-[3px] bg-[#ecebe6]">
              <div className="h-1.5 rounded-[3px] bg-forest" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="rounded-[10px] border border-[#e6e3dc] p-3">
            <div className="text-[13px] font-semibold text-navy">Later · Network model</div>
            <div className="mt-1 text-[13px] leading-[1.45] text-[#5b6168]">Acceptance and RETAIN across many clinics</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * What the patient sees
 * ------------------------------------------------------------------ */

function PhonePreview({ rows, firstName, lastName }: { rows: OprItem[]; firstName: string; lastName: string }) {
  const banner = pricingBanner(bannerLines(rows));
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="flex w-full max-w-[390px] shrink-0 flex-col gap-3" data-testid="phone-preview">
      <div className="flex items-baseline justify-between">
        <span className="text-[16px] font-semibold text-navy">What {firstName} sees</span>
        <span className="text-[13px] text-[#5b6168]">Updates as you type</span>
      </div>
      <div className="flex h-[780px] w-full max-w-[390px] flex-col overflow-hidden rounded-[52px] border-[10px] border-[#1c2b3a] bg-[#f7f6f2]">
        <div className="flex shrink-0 items-center justify-between px-[22px] pb-3 pt-[26px]">
          <div>
            <div className="font-display text-[30px] font-semibold leading-none text-navy">MCOS</div>
            <div className="mt-1 text-[8px] uppercase tracking-[0.14em] text-navy">Metabolic health. Real progress.</div>
          </div>
          <div className="flex items-center gap-3 text-navy">
            <span className="relative flex">
              <Bell size={22} />
              <span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-[#b42318]" />
            </span>
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#eeede8] font-display font-semibold">{initials}</span>
            <Menu size={22} />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 overflow-hidden px-[18px] py-1.5">
          <div className="font-display text-[24px] font-semibold text-navy">Your treatment plan</div>
          <div className="rounded-xl border border-[#e6e3dc] bg-white p-3.5">
            {rows.map((item) => (
              <div key={item.itemId} className="flex items-center gap-2.5 border-b border-[#efede7] py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[16px] font-semibold text-navy">{item.name}</div>
                  <div className="text-[12px] text-[#5b6168]">{item.howToTake}</div>
                </div>
                <div className="text-[15px] font-semibold text-navy">
                  {item.clinic.priceCents != null ? `${usd(item.clinic.priceCents)}${item.unitLabel}` : <span className="font-normal text-[#a3a8ad]">Not set</span>}
                </div>
              </div>
            ))}
            <div className="flex items-baseline justify-between pt-3">
              <span className="text-[14px] text-[#5b6168]">Monthly total</span>
              <span className="font-display text-[22px] font-semibold text-navy">{usd(banner.monthlyCents)}/mo</span>
            </div>
            {banner.perVisitCents > 0 && (
              <div className="text-right text-[12px] text-[#5b6168]">+ {usd(banner.perVisitCents)} per in-clinic visit</div>
            )}
          </div>
          <div className="flex h-[50px] items-center justify-center rounded-xl bg-forest text-[16px] font-semibold text-white">Accept plan</div>
          <div className="flex h-11 items-center justify-center rounded-xl text-[14px] text-navy">Not right now</div>
        </div>
      </div>
    </div>
  );
}
